import { IncludeFeesInBalance } from "../views/IncludeFeesInBalance";
import { Stack } from "@mui/material";
import { Dispatch, SetStateAction } from "react";
import { ExpandToggleButton, MergePositionsAcrossPlatformsToggleButton, } from "../views";
import {
    PositionImpliedVolatilitySelector
} from "./position-implied-volatility-selector/PositionImpliedVolatilitySelector";
import {
    PositionImpliedVolatilityChangingParameters
} from "./position-implied-volatility-selector/PositionImpliedVolatilityChangingParameters";
import { CustomPriceSelector } from "./CustomPriceSelector";
import { AssetType } from "../../../shared/types/AssetType";
import { TargetPlatform } from "../../../shared/types/TargetPlatform";
import { PositionStateFilter } from "../../../shared/types/PositionStateFilter";
import { PositionFilterAvailability } from "../utils/PositionFilterUtils";
import { AssetTypeFilterSelect } from "./AssetTypeFilterSelect";
import { FilterResetButton } from "./FilterResetButton";
import { TargetPlatformFilterSelect } from "./TargetPlatformFilterSelect";
import { ToolsPanelFilterSelect } from "./ToolsPanelFilterSelect";

type PositionsToolsPanelProps = {
    selectedPositionStateFilter: PositionStateFilter;
    setSelectedPositionStateFilter: Dispatch<SetStateAction<PositionStateFilter>>;
    selectedAssetTypeFilter: AssetType | null;
    setSelectedAssetTypeFilter: Dispatch<SetStateAction<AssetType | null>>;
    selectedTargetPlatformFilter: TargetPlatform | null;
    setSelectedTargetPlatformFilter: Dispatch<SetStateAction<TargetPlatform | null>>;
    filterAvailability: PositionFilterAvailability;
    resetFilters: () => void;
    mergePositionsAcrossPlatforms: boolean;
    setMergePositionsAcrossPlatforms: React.Dispatch<React.SetStateAction<boolean>>;
    includeFeesInBalance: boolean;
    setIncludeFeesInBalance: Dispatch<SetStateAction<boolean>>;
    isExpanded: boolean;
    setIsExpanded: Dispatch<SetStateAction<boolean>>;
    positionImpliedVolatilityChangingParameters: PositionImpliedVolatilityChangingParameters;
};

export function PositionsToolsPanel({
    selectedPositionStateFilter,
    setSelectedPositionStateFilter,
    selectedAssetTypeFilter,
    setSelectedAssetTypeFilter,
    selectedTargetPlatformFilter,
    setSelectedTargetPlatformFilter,
    filterAvailability,
    resetFilters,
    mergePositionsAcrossPlatforms,
    setMergePositionsAcrossPlatforms,
    includeFeesInBalance,
    setIncludeFeesInBalance,
    isExpanded,
    setIsExpanded,
    positionImpliedVolatilityChangingParameters,
}: PositionsToolsPanelProps){
    const positionStateOptions = [
        { value: 'All', label: 'All states' },
        { value: 'Open', label: 'Open', disabled: !filterAvailability.positionStates.has(PositionStateFilter.Open) },
        { value: 'Closed', label: 'Closed', disabled: !filterAvailability.positionStates.has(PositionStateFilter.Closed) },
    ];

    const handlePositionStateFilterChange = (value: string): void => {
        setSelectedPositionStateFilter(
            PositionStateFilter[value as keyof typeof PositionStateFilter]);
    };

    const isAnyFilterApplied =
        selectedPositionStateFilter !== PositionStateFilter.All ||
        selectedAssetTypeFilter !== null ||
        selectedTargetPlatformFilter !== null ||
        mergePositionsAcrossPlatforms;

    return(
        <Stack
            direction="row"
            spacing={1}
        >
            <Stack direction="row" spacing={0.5}>
                <FilterResetButton
                    isAnyFilterApplied={isAnyFilterApplied}
                    resetFilters={resetFilters}
                />
                <ToolsPanelFilterSelect
                    id="positionStateFilter"
                    value={PositionStateFilter[selectedPositionStateFilter]}
                    options={positionStateOptions}
                    width={95}
                    isFilterApplied={selectedPositionStateFilter !== PositionStateFilter.All}
                    fallbackLabel="All states"
                    onChange={handlePositionStateFilterChange}
                />
                <TargetPlatformFilterSelect
                    id="positionsTargetPlatformFilter"
                    selectedTargetPlatformFilter={selectedTargetPlatformFilter}
                    setSelectedTargetPlatformFilter={setSelectedTargetPlatformFilter}
                    availableTargetPlatforms={filterAvailability.targetPlatforms}
                />
                <AssetTypeFilterSelect
                    id="positionsAssetTypeFilter"
                    selectedAssetTypeFilter={selectedAssetTypeFilter}
                    setSelectedAssetTypeFilter={setSelectedAssetTypeFilter}
                    availableAssetTypes={filterAvailability.assetTypes}
                />
                <MergePositionsAcrossPlatformsToggleButton
                    isActivated={mergePositionsAcrossPlatforms}
                    setIsActivated={setMergePositionsAcrossPlatforms}
                />
            </Stack>
            <PositionImpliedVolatilitySelector parameters={positionImpliedVolatilityChangingParameters} />
            <CustomPriceSelector />
            <IncludeFeesInBalance
                isActivated={includeFeesInBalance}
                setIsActivated={setIncludeFeesInBalance}
            />
            <ExpandToggleButton
                isActivated={isExpanded}
                setIsActivated={setIsExpanded}
                tooltipTextWhenActivated="Restore widget"
                tooltipTextWhenDeactivated="Maximise widget"
            />
        </Stack>
    )
}
